import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useSupabase, useTenant } from "@how/app-kit";
import type { Enums, Json, Tables } from "@how/database";
import { z } from "zod";

function useTenantId(): string | null {
  const tenant = useTenant();
  return tenant.status === "resolved" ? tenant.tenant.tenantId : null;
}

/** A field on a form, as the church defined it. The database checks this shape too. */
export interface FormField {
  readonly key: string;
  readonly label: string;
  readonly type: Enums<"form_field_type">;
  readonly required?: boolean | undefined;
  readonly options?: readonly string[] | undefined;
  /** For a consent field: what the visitor is consenting to receive. */
  readonly channel?: string | undefined;
}

export type FormRow = Pick<
  Tables<"content_forms">,
  "id" | "name" | "description" | "fields" | "is_active" | "thank_you_message" | "created_at"
> & {
  readonly submissions: number;
  readonly unread: number;
};

export function formFields(fields: Json | null): FormField[] {
  return Array.isArray(fields) ? (fields as unknown as FormField[]) : [];
}

/** The church's forms, with how much has arrived and how much nobody has read. */
export function useForms() {
  const supabase = useSupabase();
  const tenantId = useTenantId();
  return useQuery({
    queryKey: ["forms", tenantId],
    enabled: tenantId !== null,
    queryFn: async (): Promise<FormRow[]> => {
      const { data, error } = await supabase
        .from("content_forms")
        .select(
          "id, name, description, fields, is_active, thank_you_message, created_at, content_form_submissions(count)",
        )
        .eq("tenant_id", tenantId ?? "")
        .is("deleted_at", null)
        .order("name");
      if (error) throw error;
      const unread = await supabase
        .from("content_form_submissions")
        .select("form_id")
        .eq("tenant_id", tenantId ?? "")
        .eq("status", "new");
      if (unread.error) throw unread.error;
      const newPerForm = new Map<string, number>();
      for (const row of unread.data) {
        newPerForm.set(row.form_id, (newPerForm.get(row.form_id) ?? 0) + 1);
      }
      return data.map((row) => ({
        id: row.id,
        name: row.name,
        description: row.description,
        fields: row.fields,
        is_active: row.is_active,
        thank_you_message: row.thank_you_message,
        created_at: row.created_at,
        submissions: row.content_form_submissions[0]?.count ?? 0,
        unread: newPerForm.get(row.id) ?? 0,
      }));
    },
  });
}

export type SubmissionRow = Pick<
  Tables<"content_form_submissions">,
  | "id"
  | "data"
  | "consent_channels"
  | "status"
  | "submitted_at"
  | "handled_at"
  | "person_id"
  | "match_outcome"
  | "match_details"
>;

export interface FormDetail {
  readonly form: Tables<"content_forms">;
  readonly submissions: readonly SubmissionRow[];
}

/** One form and what has arrived through it, newest first. */
export function useForm(formId: string | null) {
  const supabase = useSupabase();
  const tenantId = useTenantId();
  return useQuery({
    queryKey: ["form", tenantId, formId],
    enabled: formId !== null,
    queryFn: async (): Promise<FormDetail> => {
      const id = formId ?? "";
      const [form, submissions] = await Promise.all([
        supabase.from("content_forms").select("*").eq("id", id).single(),
        supabase
          .from("content_form_submissions")
          .select(
            "id, data, consent_channels, status, submitted_at, handled_at, person_id, match_outcome, match_details",
          )
          .eq("form_id", id)
          .order("submitted_at", { ascending: false })
          .limit(500),
      ]);
      if (form.error) throw form.error;
      if (submissions.error) throw submissions.error;
      return { form: form.data, submissions: submissions.data };
    },
  });
}

function useFormsInvalidation() {
  const client = useQueryClient();
  const tenantId = useTenantId();
  return async (formId?: string) => {
    await client.invalidateQueries({ queryKey: ["forms", tenantId] });
    if (formId !== undefined)
      await client.invalidateQueries({ queryKey: ["form", tenantId, formId] });
  };
}

const failed = (error: { message: string; code?: string } | null) => {
  if (error !== null) throw Object.assign(new Error(error.message), { code: error.code });
};

export interface FormFields {
  readonly name: string;
  readonly description: string;
  readonly thankYouMessage: string;
  readonly isActive: boolean;
  /** "This form is for adults (18 and over)": a yes to texts is recorded at submit only then (D1, #338). */
  readonly forAdults: boolean;
  readonly fields: readonly FormField[];
}

export function useCreateForm() {
  const supabase = useSupabase();
  const tenantId = useTenantId();
  const invalidate = useFormsInvalidation();
  return useMutation({
    mutationFn: async (input: { readonly name: string }): Promise<string> => {
      const { data, error } = await supabase.rpc("create_form", {
        p_tenant_id: tenantId ?? "",
        p_name: input.name,
      });
      failed(error);
      return data as string;
    },
    onSuccess: async () => {
      await invalidate();
    },
  });
}

export function useUpdateForm() {
  const supabase = useSupabase();
  const invalidate = useFormsInvalidation();
  return useMutation({
    mutationFn: async (input: {
      readonly formId: string;
      readonly fields: FormFields;
      /** The setting as last saved: it is sent only when it changed, by its own function. */
      readonly savedForAdults: boolean;
    }) => {
      const f = input.fields;
      const { error } = await supabase.rpc("update_form", {
        p_form_id: input.formId,
        p_name: f.name.trim(),
        // Plain JSON as the database checks it: a key, a label, a type, and what each type needs. Keys that do not
        // apply to a type are dropped rather than sent as null.
        p_fields: f.fields.map((field) =>
          Object.fromEntries(Object.entries(field).filter(([, value]) => value !== undefined)),
        ) as unknown as Json,
        p_description: f.description,
        p_thank_you_message: f.thankYouMessage,
        p_is_active: f.isActive,
      });
      failed(error);
      if (f.forAdults !== input.savedForAdults) {
        const adults = await supabase.rpc("set_form_for_adults", {
          p_form_id: input.formId,
          p_for_adults: f.forAdults,
        });
        failed(adults.error);
      }
    },
    onSuccess: async (_r, input) => {
      await invalidate(input.formId);
    },
  });
}

export function useDeleteForm() {
  const supabase = useSupabase();
  const invalidate = useFormsInvalidation();
  return useMutation({
    mutationFn: async (formId: string) => {
      const { error } = await supabase.rpc("delete_form", { p_form_id: formId });
      failed(error);
    },
    onSuccess: async () => {
      await invalidate();
    },
  });
}

export function useSetSubmissionStatus() {
  const supabase = useSupabase();
  const invalidate = useFormsInvalidation();
  return useMutation({
    mutationFn: async (input: {
      readonly formId: string;
      readonly submissionId: string;
      readonly status: Enums<"form_submission_status">;
    }) => {
      const { error } = await supabase.rpc("set_submission_status", {
        p_submission_id: input.submissionId,
        p_status: input.status,
      });
      failed(error);
    },
    onSuccess: async (_r, input) => {
      await invalidate(input.formId);
    },
  });
}

export function useDeleteSubmission() {
  const supabase = useSupabase();
  const invalidate = useFormsInvalidation();
  return useMutation({
    mutationFn: async (input: { readonly formId: string; readonly submissionId: string }) => {
      const { error } = await supabase.rpc("delete_submission", {
        p_submission_id: input.submissionId,
      });
      failed(error);
    },
    onSuccess: async (_r, input) => {
      await invalidate(input.formId);
    },
  });
}

const preview = z.object({
  firstName: z.string().nullable(),
  lastName: z.string().nullable(),
  email: z.string().nullable(),
  phone: z.string().nullable(),
  consent: z.array(z.enum(["email", "sms", "phone", "mail"])),
  formName: z.string(),
  sentAt: z.string(),
  personId: z.string().nullable(),
  personName: z.string().nullable(),
  matches: z.array(
    z.object({
      personId: z.string(),
      name: z.string(),
      matchedOn: z.array(z.enum(["email", "phone"])),
      /** The number they are texted at (their main phone), or null when they have none. */
      textsTo: z.string().nullable(),
      /** Whether that is the form's number, compared as the database compares numbers. */
      sameTextsNumber: z.boolean(),
    }),
  ),
});
export type SubmissionPreview = z.infer<typeof preview>;

/**
 * What "Add this person" would do (#304): the name, email and number read from the submission, the consent it gave,
 * and anyone already in the church with the same email or number. The database reads the form; it is the one reader.
 */
export function useSubmissionPreview(submissionId: string) {
  const supabase = useSupabase();
  const tenantId = useTenantId();
  return useQuery({
    queryKey: ["submission-preview", tenantId, submissionId],
    retry: false,
    queryFn: async (): Promise<SubmissionPreview> => {
      const { data, error } = await supabase.rpc("submission_person_preview", {
        p_submission_id: submissionId,
      });
      failed(error);
      return preview.parse(data);
    },
  });
}

const added = z.object({
  personId: z.string(),
  created: z.boolean(),
  recorded: z.array(z.enum(["email", "sms", "phone", "mail"])),
  kept: z.array(
    z.object({
      channel: z.enum(["email", "sms", "phone", "mail"]),
      optedIn: z.boolean(),
      recordedAt: z.string(),
    }),
  ),
  /**
   * Consent the form gave that was not recorded, and why: a yes to texts follows the number it was given for, so it
   * is held back when the form gave no number or the person is texted at another one.
   */
  withheld: z.array(
    z.object({
      channel: z.enum(["email", "sms", "phone", "mail"]),
      reason: z.enum(["not_main_number", "no_number"]),
      textsTo: z.string().nullable(),
    }),
  ),
  /** Whether the form's number was made the person's main number for texts. */
  madePrimary: z.boolean(),
  contactsAdded: z.number(),
  /** What the consent was recorded as coming from: the form and the day it was sent. */
  source: z.string(),
});
export type AddedFromSubmission = z.infer<typeof added>;

/**
 * Adds the person a submission came from, or attaches it to someone already known, with the consent it gave (#304).
 * The People list and the form both change, so both are read again.
 */
export function useAddPersonFromSubmission() {
  const supabase = useSupabase();
  const tenantId = useTenantId();
  const client = useQueryClient();
  const invalidate = useFormsInvalidation();
  return useMutation({
    mutationFn: async (input: {
      readonly formId: string;
      readonly submissionId: string;
      readonly personId?: string | undefined;
      readonly firstName?: string | undefined;
      readonly lastName?: string | undefined;
      /** Staff said yes to "Make … the main number for texts?". */
      readonly makeFormPhonePrimary?: boolean | undefined;
    }): Promise<AddedFromSubmission> => {
      const { data, error } = await supabase.rpc("create_person_from_submission", {
        p_submission_id: input.submissionId,
        ...(input.personId === undefined ? {} : { p_person_id: input.personId }),
        ...(input.firstName === undefined ? {} : { p_first_name: input.firstName }),
        ...(input.lastName === undefined ? {} : { p_last_name: input.lastName }),
        ...(input.makeFormPhonePrimary === undefined
          ? {}
          : { p_make_form_phone_primary: input.makeFormPhonePrimary }),
      });
      failed(error);
      return added.parse(data);
    },
    onSuccess: async (_r, input) => {
      await invalidate(input.formId);
      await client.invalidateQueries({ queryKey: ["submission-preview", tenantId] });
      await client.invalidateQueries({ queryKey: ["people", tenantId] });
    },
  });
}

const matchDetails = z.object({
  /** Why a sign-up was left for staff (#338 part 2). */
  reason: z
    .enum([
      "several_match",
      "name_differs",
      "same_name",
      "child",
      "no_full_name",
      "nothing_to_match",
      "could_not_match",
      "no_email_to_confirm",
    ])
    .optional(),
  matchCount: z.number().optional(),
  /** Why a yes to texts was not recorded at submit. */
  textConsentWaits: z
    .enum(["not_for_adults", "not_main_number", "no_number", "opted_out"])
    .optional(),
  recorded: z.array(z.enum(["email", "sms", "phone", "mail"])).optional(),
  /** Contact details the form gave that differ from the person's: left unchanged, for staff. */
  differs: z.array(z.enum(["email", "phone"])).optional(),
  contactsAdded: z.number().optional(),
  /** What waits for the person to confirm by email (ADR 0063): codes, never values. */
  held: z
    .object({
      contacts: z.array(z.enum(["email", "phone", "texted_phone"])),
      consent: z.array(z.enum(["email", "sms", "phone", "mail"])),
    })
    .optional(),
  /** Consent the person answered again after the form: their newer answer stayed. */
  keptNewer: z.array(z.enum(["email", "sms", "phone", "mail"])).optional(),
  /** How held details were applied: the person confirmed by email, or staff added them. */
  confirmedBy: z.enum(["email", "staff"]).optional(),
  confirmedAt: z.string().optional(),
});
export type MatchDetails = z.infer<typeof matchDetails>;
export type MatchOutcome = "matched" | "created" | "needs_decision" | "awaiting_confirmation";

/**
 * What happened when a sign-up arrived (#338 part 2): matched to someone, added as a new person, or left for staff,
 * with the codes the database wrote. Anything it does not recognise reads as nothing happened, never as a failure.
 */
export function submissionMatch(row: Pick<SubmissionRow, "match_outcome" | "match_details">): {
  readonly outcome: MatchOutcome | null;
  readonly details: MatchDetails;
} {
  const outcome =
    row.match_outcome === "matched" ||
    row.match_outcome === "created" ||
    row.match_outcome === "needs_decision" ||
    row.match_outcome === "awaiting_confirmation"
      ? row.match_outcome
      : null;
  const parsed = matchDetails.safeParse(row.match_details);
  return { outcome, details: parsed.success ? parsed.data : {} };
}

/**
 * Records a yes to texts that waited at submit because the form isn't marked for adults, once staff know the person
 * is an adult (#338 part 2). The database checks the number, a child, and a newer answer, and refuses in words.
 */
export function useRecordSubmissionTextConsent() {
  const supabase = useSupabase();
  const tenantId = useTenantId();
  const client = useQueryClient();
  const invalidate = useFormsInvalidation();
  return useMutation({
    mutationFn: async (input: { readonly formId: string; readonly submissionId: string }) => {
      const { error } = await supabase.rpc("record_submission_text_consent", {
        p_submission_id: input.submissionId,
      });
      failed(error);
    },
    onSuccess: async (_r, input) => {
      await invalidate(input.formId);
      await client.invalidateQueries({ queryKey: ["people", tenantId] });
    },
  });
}

const confirmation = z
  .object({
    status: z.enum([
      "queued",
      "sending",
      "sent",
      "failed",
      "blocked",
      "confirmed",
      "applied_by_staff",
      "cancelled",
    ]),
    blockedReason: z.enum(["no_address", "test_address", "child", "gone"]).nullable(),
    personId: z.string(),
    /** Null when the reader may not see the person (people.read; a child, people.read_minors). */
    personName: z.string().nullable(),
    sentAt: z.string().nullable(),
    sendCount: z.number(),
    confirmedAt: z.string().nullable(),
    /** The link is more than seven days old. */
    expired: z.boolean(),
    /** "Send again" is allowed from this moment (ten minutes after the last). */
    sendAgainFrom: z.string().nullable(),
    sendsLeft: z.number(),
  })
  .nullable();
export type SubmissionConfirmation = z.infer<typeof confirmation>;

/**
 * Where a held sign-up's confirmation email is (ADR 0063): sent when, how many more may go, and who it is for. The
 * database reads it as the caller, so the policies decide what is shown.
 */
export function useSubmissionConfirmation(submissionId: string) {
  const supabase = useSupabase();
  const tenantId = useTenantId();
  return useQuery({
    queryKey: ["submission-confirmation", tenantId, submissionId],
    retry: false,
    queryFn: async (): Promise<SubmissionConfirmation> => {
      const { data, error } = await supabase.rpc("submission_confirmation", {
        p_submission_id: submissionId,
      });
      failed(error);
      return confirmation.parse(data);
    },
  });
}

/** "Send again": a new email with a new link; the database keeps the ten minutes and the three emails. */
export function useResendFormConfirmation() {
  const supabase = useSupabase();
  const tenantId = useTenantId();
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (input: { readonly submissionId: string }) => {
      const { error } = await supabase.rpc("resend_form_confirmation", {
        p_submission_id: input.submissionId,
      });
      failed(error);
    },
    onSuccess: async (_r, input) => {
      await client.invalidateQueries({
        queryKey: ["submission-confirmation", tenantId, input.submissionId],
      });
    },
  });
}

/**
 * "Add these details now": staff apply what a sign-up holds, for someone who confirmed another way. The database
 * checks forms.manage, people.write and preferences.manage, a child, and each change again.
 */
export function useApplySubmissionHeldChanges() {
  const supabase = useSupabase();
  const tenantId = useTenantId();
  const client = useQueryClient();
  const invalidate = useFormsInvalidation();
  return useMutation({
    mutationFn: async (input: { readonly formId: string; readonly submissionId: string }) => {
      const { error } = await supabase.rpc("apply_submission_held_changes", {
        p_submission_id: input.submissionId,
      });
      failed(error);
    },
    onSuccess: async (_r, input) => {
      await invalidate(input.formId);
      await client.invalidateQueries({ queryKey: ["submission-preview", tenantId] });
      await client.invalidateQueries({
        queryKey: ["submission-confirmation", tenantId, input.submissionId],
      });
      await client.invalidateQueries({ queryKey: ["people", tenantId] });
    },
  });
}
